<script>
import validations from './helpers/validations';
import { mapGetters } from 'vuex';

import ButtonNext from 'next/button/Button.vue';
import Spinner from 'next/spinner/Spinner.vue';
import SourceChunk from './components/ChunkCard.vue';
import CreateBotSourceChunkDialog from './components/CreateBotSourceChunkDialog.vue';

export default {
  components: {
    ButtonNext,
    Spinner,
    SourceChunk,
    CreateBotSourceChunkDialog,
  },
  props: {
    bot: {
      type: Object,
      default: () => ({}),
    },
    sourceId: {
      type: Number,
      default: 0,
    },
  },
  emits: ['deleteChunk', 'editChunk', 'toggleChunkStatus'],
  data() {
    return {
      searchValue: '',
      selectedTabIndex: 0,
      tableHeaders: [
        this.$t(
          'WGPT_BOTS.EDIT.TABS.KNOWLEDGE_BASE.TABS.ITEMS.LIST.TABLE_HEADER.ITEM'
        ),
        this.$t(
          'WGPT_BOTS.EDIT.TABS.KNOWLEDGE_BASE.TABS.ITEMS.LIST.TABLE_HEADER.TYPE'
        ),
      ],
      selectedSource: {},
    };
  },
  computed: {
    ...mapGetters({
      chunks: 'wgptBotSourceChunks/getSourceChunks',
    }),
    source() {
      return this.$store.getters['wgptBotSources/getSource'](this.sourceId);
    },
    parameters() {
      if (!this.source) return [];

      const parameters = this.source.parameters || {};
      return Object.entries(parameters).map(([key, value]) => ({ key, value }));
    },
    selectedTabKey() {
      return this.tabs[this.selectedTabIndex]?.key;
    },
    tabs() {
      return [
        {
          key: 'items',
          name: this.$t('WGPT_BOTS.EDIT.TABS.KNOWLEDGE_BASE.TABS.ITEMS.NAME'),
        },
        {
          key: 'search',
          name: this.$t('WGPT_BOTS.EDIT.TABS.KNOWLEDGE_BASE.TABS.SEARCH.NAME'),
        },
        {
          key: 'actions',
          name: '',
        },
      ];
    },
  },
  watch: {
    sourceId: {
      handler() {
        if (!this.sourceId) return;
        this.getChunks();
      },
      immediate: true,
    },
    bot() {
      this.setDefaults();
    },
  },
  mounted() {
    this.setDefaults();
  },
  validations,
  methods: {
    setDefaults() {},
    onTabChange(selectedTabIndex) {
      this.selectedTabIndex = selectedTabIndex;
    },
    openAddPopup() {
      this.$refs.createBotSourceChunkDialog.open(this.bot, this.source);
    },
    getChunks() {
      const botId = this.bot.id;
      const sourceId = this.sourceId;
      this.$store.dispatch('wgptBotSourceChunks/get', { botId, sourceId });
    },
  },
};
</script>

<template>
  <div class="w-full h-full overflow-auto">
    <div class="w-full h-full">
      <div v-if="sourceId" class="flex flex-col gap-8 p-8">
        <div class="flex flex-col gap-4">
          <div>
            <span class="text-n-slate-11 text-xs">
              {{
                $t(
                  'WGPT_BOTS.EDIT.TABS.KNOWLEDGE_BASE.TABS.ITEMS.SOURCE.NAME.LABEL'
                )
              }}
            </span>
            <div class="mt-2 text-xl font-medium text-n-slate-12">
              {{ source.name || '—' }}
            </div>
          </div>

          <div>
            <span class="text-n-slate-11 text-xs">
              {{
                $t(
                  'WGPT_BOTS.EDIT.TABS.KNOWLEDGE_BASE.TABS.ITEMS.SOURCE.DESCRIPTION.LABEL'
                )
              }}
            </span>
            <div class="mt-2 text-sm text-n-slate-12">
              {{ source.description || '—' }}
            </div>
          </div>
        </div>

        <div>
          <span class="py-1 text-sm font-medium text-n-slate-12">
            {{
              $t(
                'WGPT_BOTS.EDIT.TABS.KNOWLEDGE_BASE.TABS.ITEMS.SOURCE.PARAMETERS.LABEL'
              )
            }}
          </span>
          <div class="mt-2 flex gap-2">
            <template v-if="parameters.length > 0">
              <ButtonNext
                v-for="parameter in parameters"
                :key="parameter.key"
                :label="`${parameter.key}: ${parameter.value}`"
                variant="faded"
                size="sm"
              />
            </template>
            <template v-else>
              <p class="text-sm text-slate-600 dark:text-slate-400">
                {{
                  $t(
                    'WGPT_BOTS.EDIT.TABS.KNOWLEDGE_BASE.TABS.ITEMS.SOURCE.PARAMETERS.EMPTY'
                  )
                }}
              </p>
            </template>
          </div>
        </div>

        <div class="h-px bg-n-strong" />

        <div class="flex flex-col gap-4">
          <div>
            <span class="text-base font-medium text-n-slate-12">
              {{
                $t(
                  'WGPT_BOTS.EDIT.TABS.KNOWLEDGE_BASE.TABS.ITEMS.SOURCE.SOURCE_CHUNKS.LABEL'
                )
              }}
            </span>
            <p class="text-sm mt-1 text-n-slate-11">
              {{
                $t(
                  'WGPT_BOTS.EDIT.TABS.KNOWLEDGE_BASE.TABS.ITEMS.SOURCE.SOURCE_CHUNKS.SUB_LABEL'
                )
              }}
            </p>
          </div>

          <template v-if="chunks">
            <div v-if="chunks.length" class="flex flex-col gap-4">
              <SourceChunk
                v-for="chunk of chunks"
                :key="chunk.id"
                :chunk="chunk"
                @delete="$emit('deleteChunk', chunk)"
                @toggle-status="$emit('toggleChunkStatus', chunk)"
                @edit="$emit('editChunk', chunk)"
              />
            </div>

            <template v-else>
              <p class="my-6 text-sm text-slate-600 dark:text-slate-400">
                {{
                  $t(
                    'WGPT_BOTS.EDIT.TABS.KNOWLEDGE_BASE.TABS.ITEMS.SOURCE.SOURCE_CHUNKS.EMPTY'
                  )
                }}
              </p>
            </template>

            <div>
              <ButtonNext
                icon="i-lucide-plus"
                size="sm"
                :label="
                  $t(
                    'WGPT_BOTS.EDIT.TABS.KNOWLEDGE_BASE.TABS.ITEMS.SOURCE.SOURCE_CHUNKS.ADD'
                  )
                "
                @click="openAddPopup"
              />
            </div>
          </template>

          <div
            v-else
            class="flex items-center justify-center py-10 text-n-slate-11"
          >
            <Spinner />
          </div>
        </div>
      </div>

      <template v-else>
        <div
          class="position-absolute w-full pb-5 flex flex-col items-center justify-center h-full"
        >
          <span class="text-sm font-medium text-slate-500 dark:text-slate-300">
            {{
              $t(
                'WGPT_BOTS.EDIT.TABS.KNOWLEDGE_BASE.TABS.ITEMS.LIST.SELECT_A_SOURCE'
              )
            }}
            <br />
          </span>
        </div>
      </template>

      <CreateBotSourceChunkDialog ref="createBotSourceChunkDialog" />
    </div>
  </div>
</template>
